import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useLDUser from '@app/hooks/useLDUser';
import { getArtemisWebUrl } from '@app/helpers/environment';
import {
  hasRedirectedToArtemisWeb,
  markRedirectedToArtemisWeb,
} from '@app/constants/artemisRedirectGate';

/**
 * Sends flagged-in users to artemis-web once LaunchDarkly has the real user context.
 *
 * `app.jsx` calls asyncWithLDProvider with no context, so the SDK boots an anonymous
 * one and `artemisWeb` already has a value before login. Acting on that value would
 * redirect by anonymous-key rollout rather than by cohort.
 *
 * `isLdUserIdentified` alone is not enough to rule that out: when profileId or
 * companyId is missing, setFeatureFlagUser identifies with the anonymous context
 * (app/store/appState/sagas.js) and still resolves, so the saga reaches
 * fetchInitialDataSuccess and flips the flag with LD on an anonymous context. The
 * trusted signal is the identified context itself being the multi-kind one the saga
 * builds — hence the `kind === 'multi'` check.
 *
 * `initialDataAlreadyFetched` is set by both the success and error reducers, so a
 * bootstrap error releases the render block instead of stranding the user on a
 * spinner. (The saga's super-admin and shouldLogout early returns dispatch neither,
 * but both leave for routes outside this gate.) In that state we render bennie-web
 * and never redirect.
 *
 * Deep links are not forwarded: artemis-web route parity is not guaranteed, so
 * everyone lands on its root.
 *
 * @returns {{ shouldBlockRender: boolean }} true while the redirect decision is unknown,
 *   and while a redirect is in flight, so the caller can hold a loader instead of
 *   flashing bennie-web UI.
 */
const useArtemisWebRedirect = () => {
  const { artemisWeb } = useFlags();
  const { context, isLdUserIdentified } = useLDUser();
  const initialDataSettled = useSelector(
    state => state.appState.initialDataAlreadyFetched,
  );

  // Freeze the gate at mount. We write the marker before navigating, so re-reading it
  // would flip shouldRedirect back to false mid-navigation and drop the loader,
  // flashing bennie-web while the browser is already leaving.
  const alreadyRedirected = useRef(null);
  if (alreadyRedirected.current === null) {
    alreadyRedirected.current = hasRedirectedToArtemisWeb();
  }

  const hasIdentifiedUserContext =
    isLdUserIdentified && context?.kind === 'multi' && !context?.anonymous;

  const shouldRedirect = Boolean(
    hasIdentifiedUserContext && artemisWeb && !alreadyRedirected.current,
  );
  const isDecisionPending = !isLdUserIdentified && !initialDataSettled;

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }

    markRedirectedToArtemisWeb();
    // replace() so bennie-web leaves no history entry to back into.
    window.location.replace(getArtemisWebUrl());
  }, [shouldRedirect]);

  return { shouldBlockRender: isDecisionPending || shouldRedirect };
};

export default useArtemisWebRedirect;
